import {
  Button,
  Box,
  Text,
  TextLink,
  Bleed,
  Actions,
  Skeleton,
  useToast
} from '@machobear/design-system'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'ramda'
import { useGetFilteredPackages } from '@/hooks/use-get-package-list'
import { Dropdown } from '@/components/ds-form'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import { DialogEventHandler } from '@/types'

type Props = {
  closeModal: DialogEventHandler
}
type Package = {
  id: string
  metadata: {
    package_name: string
  }
}

const ModalContent = ({ closeModal }: Props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const showToast = useToast()

  const {
    data: sponsoredListingsPackages,
    isLoading,
    error
  } = useGetFilteredPackages({
    searchKey: 'sponsored_listings'
  })
  const planText = useGetPlanKey()
  const clickId = `usage-credit-${planText}-buycredits-pkg-dialog-multiplatform-adv-continue`

  const noPackages = isEmpty(sponsoredListingsPackages) && !isLoading && !error

  useEffect(() => {
    if (error || noPackages) {
      closeModal()
      showToast({
        message: (
          <Trans i18nKey="l.usage_credits.purchase.error.text">
            Sorry! An unexpected error has occured. Please try again later or contact
            <TextLink href="mailto:support@liv.rent">support@liv.rent.</TextLink>
          </Trans>
        ),
        tone: 'critical'
      })
    }
  }, [error, noPackages])

  const onSubmit = async (
    values: { package: string },
    { setSubmitting }: FormikHelpers<{ package: string }>
  ) => {
    history.push(`/promotions/${values.package}`)
    setSubmitting(false)
    closeModal()
  }

  const initialValues = {
    package: sponsoredListingsPackages?.[0]?.id
  }
  const formattedPackages = sponsoredListingsPackages?.map((item: Package) => ({
    value: item?.id,
    label: item?.metadata?.package_name
  }))

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={Yup.object().shape({
        package: Yup.string().required(
          t('l.usage.credits.error.text', 'Please select at least 1 listing to continue.')
        )
      })}
    >
      {({ values, handleSubmit, setFieldValue, errors, touched }) => (
        <Form>
          <Box style={{ marginBottom: '12px', marginTop: '16px' }}>
            <Text size="medium" weight="medium">
              {t('l.usage.credits.select_a_package.text', 'Select a package')}
            </Text>
          </Box>
          <Box paddingBottom="xxxsmall">
            {isLoading ? (
              <Skeleton.Shape height={58} width={'100%'} />
            ) : (
              <Dropdown
                name="package"
                label={t('l.usage.credits.package.text', 'Package')}
                options={formattedPackages}
                searchable={false}
              />
            )}
          </Box>
          <Bleed horizontal="large" bottom="large">
            <Box background="body" paddingX="large" paddingY="large">
              <Actions align={'right'}>
                <Button
                  type="submit"
                  variant="solid"
                  onClick={handleSubmit}
                  size="small"
                  id={clickId}
                  disabled={isLoading || !values.package}
                >
                  {t('b.usage.dialog.continue.title', 'Continue')}
                </Button>
              </Actions>
            </Box>
          </Bleed>
        </Form>
      )}
    </Formik>
  )
}

export default ModalContent
